import { useEffect, useRef, useState } from "react";
import { useMp3Controller } from "./useMp3Controller";

export function useMp3Player(id, mp3File) {
  const controller = useMp3Controller(); // may be null
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isEnded, setIsEnded] = useState(false);
  const [currentPlaybackPosition, setCurrentPlaybackPosition] = useState(0);

  useEffect(() => {
    if (!mp3File) {
      console.log("No mp3 file provided");
      return;
    }

    const audio = new Audio(mp3File);
    audioRef.current = audio;

    const handleEnded = () => {
      setIsPlaying(false);
      setIsEnded(true);
      controller?.handleEnded?.(id);
    };
    const handlePlay = () => {
      setIsPlaying(true);
      setIsEnded(false);
    };
    const handlePause = () => {
      setIsPlaying(false);
    };
    const handleLoadedMetadata = () => {
      setAudioDuration(audio.duration);
    };
    const updateTime = () => {
      setCurrentTime(audio.currentTime);
      setCurrentPlaybackPosition((audio.currentTime / audio.duration) * 100);
    };

    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", updateTime);

    controller?.register?.(id, audio);

    return () => {
      controller?.unregister?.(id);
      audio.pause();
      audio.src = "";
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", updateTime);
    };
    // We don't want to include the controller in the dependency array otherwise it causes the useMp3Player hook to rerender on every user event
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mp3File]);

  const play = () => {
    if (isEnded) {
      audioRef.current.currentTime = 0;
      setCurrentPlaybackPosition(0);
      setIsEnded(false);
    }

    if (controller?.play) {
      controller.play(id);
    } else {
      audioRef.current.play();
    }
    setIsPlaying(true);
  };

  const pause = () => {
    if (controller?.pause) {
      controller.pause();
    } else {
      audioRef.current.pause();
    }
    setIsPlaying(false);
  };

  const seek = (percentage) => {
    if (audioRef.current) {
      const newTime = (percentage / 100) * audioDuration;
      audioRef.current.currentTime = newTime;
      setCurrentPlaybackPosition(percentage);
    }
    if (controller?.getCurrentAudio) {
      const currentAudio = controller.getCurrentAudio();
      if (currentAudio) {
        const newTime = (percentage / 100) * audioDuration;
        currentAudio.currentTime = newTime;
        setCurrentPlaybackPosition(percentage);
      }
    }
    if (percentage === "100") {
      setIsEnded(true);
    } else {
      setIsEnded(false);
    }
  };

  return {
    play,
    pause,
    seek,
    isPlaying,
    audio: audioRef.current,
    audioDuration,
    currentTime,
    isEnded,
    currentPlaybackPosition,
  };
}
